import { useState, useCallback, useRef, useEffect } from 'react';
import inquiriesService from '../services/inquiriesService';
import { useMessageAlert } from '../components/common/MessageAlert';
import i18n from '../i18n';
import useProductSizes from './useProductSizes';
import { useWarehouseContext } from '../context/WarehouseContext';

/**
 * Hook: useInquiries
 * - Provides CRUD and list operations for inquiries
 * - References product sizes hook so UI can show product-size related data when creating an inquiry
 */
const useInquiries = () => {
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [filters, setFilters] = useState({ search: '' });

  const { showCreate, showUpdate, showDelete, showSuccess } = useMessageAlert();
  const { selectedWarehouseIds } = useWarehouseContext();

  // Reference to product sizes hook so callers get access to product size options
  const productSizesHook = useProductSizes();
  const { productSizes, fetchProductSizes } = productSizesHook || {};

  // Deduplication & in-flight tracking
  const lastSignatureRef = useRef('');
  const lastFetchTimeRef = useRef(0);
  const inFlightRef = useRef(new Set());

  const currentPageRef = useRef(1);
  const filtersRef = useRef({ search: '' });
  const pageSizeRef = useRef(20);

  const setPageSize = useCallback((size) => {
    pageSizeRef.current = size;
  }, []);

  useEffect(() => { currentPageRef.current = currentPage; }, [currentPage]);
  useEffect(() => { filtersRef.current = filters; }, [filters]);

  const generateSignature = useCallback((params) => JSON.stringify(params || {}), []);

  const shouldDedupe = useCallback((signature, force) => {
    if (force) return false;
    const now = Date.now();
    const timeDiff = now - lastFetchTimeRef.current;
    return signature === lastSignatureRef.current && timeDiff < 200;
  }, []);

  const fetchInquiries = useCallback(async (options = {}) => {
    const { force = false } = options;
    const page = currentPageRef.current;
    const limit = pageSizeRef.current;
    const skip = (page - 1) * limit;
    const params = {
      page,
      skip,
      limit,
      search: filtersRef.current.search,
      from_date: filtersRef.current.from_date,
      to_date: filtersRef.current.to_date,
    };

    const signature = generateSignature({ ...params, warehouse_ids: selectedWarehouseIds });

    if (shouldDedupe(signature, force)) return { success: true, fromCache: true };
    if (inFlightRef.current.has(signature)) return { success: true, fromCache: true };

    setLoading(true);
    setError(null);
    inFlightRef.current.add(signature);

    try {
      const result = await inquiriesService.getAllInquiries(params, { force });
      if (result.success) {
        const data = result.data;
        // Sort by updated_at or created_at (newest first)
        const sortByDate = (arr) => {
          return [...arr].sort((a, b) => {
            const dateA = new Date(a.updated_at || a.created_at || 0);
            const dateB = new Date(b.updated_at || b.created_at || 0);
            return dateB - dateA;
          });
        };
        if (Array.isArray(data)) {
          setInquiries(sortByDate(data));
          setTotalCount(data.length);
          setTotalPages(Math.max(1, Math.ceil(data.length / limit)));
        } else if (data && Array.isArray(data.inquiries)) {
          setInquiries(sortByDate(data.inquiries));
          const totalRecords = data.total || data.inquiries.length;
          setTotalCount(totalRecords);
          setTotalPages(data.totalPages || Math.max(1, Math.ceil(totalRecords / limit)));
        } else if (data && Array.isArray(data.items)) {
          setInquiries(sortByDate(data.items));
          const totalRecords = data.total || data.count || data.items.length;
          setTotalCount(totalRecords);
          setTotalPages(data.total_pages || data.totalPages || Math.max(1, Math.ceil(totalRecords / limit)));
        } else {
          setInquiries(data || []);
          setTotalPages(1);
          setTotalCount(Array.isArray(data) ? data.length : 0);
        }

        lastSignatureRef.current = signature;
        lastFetchTimeRef.current = Date.now();
      } else {
        setError(result.error);
        setInquiries([]);
      }

      return result;
    } catch (err) {
      const errorMessage = err.message;
      setError(errorMessage);
      setInquiries([]);
      return { success: false, error: errorMessage };
    } finally {
      inFlightRef.current.delete(signature);
      setLoading(false);
    }
  }, [generateSignature, shouldDedupe, selectedWarehouseIds]);

  const getInquiryById = useCallback(async (inquiryId, options = {}) => {
    setLoading(true);
    setError(null);
    try {
      const result = await inquiriesService.getInquiryById(inquiryId, options);
      if (!result.success) setError(result.error);
      return result;
    } catch (err) {
      const errorMessage = err.message;
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  }, []);

  const normalizeInquiryPayload = (p = {}) => {
    const out = { ...p };
    if (p.freight_type) out.freight_type = String(p.freight_type).toLowerCase();
    if (p.source_type) out.source_type = String(p.source_type).toLowerCase();
    if (p.status) out.status = String(p.status).toLowerCase();
    return out;
  };

  const createInquiry = useCallback(async (payload) => {
    setError(null);
    try {
      const normalized = normalizeInquiryPayload(payload || {});
      const result = await inquiriesService.createInquiry(normalized);
      if (result.success) {
        // Prepend newly created inquiry
        setInquiries(prev => [result.data, ...prev]);
        setTotalCount(prev => prev + 1);
        const label = result.data?.id || '';
        showCreate(i18n.t('inquiriesModule.inquiries.notifications.created', { id: label }));
      } else {
        setError(result.error);
      }
      return result;
    } catch (err) {
      const errorMessage = err.message;
      setError(errorMessage);
      return { success: false, error: errorMessage };
    }
  }, [showCreate]);

  const updateInquiry = useCallback(async (inquiryId, payload) => {
    setError(null);
    try {
      const normalized = normalizeInquiryPayload(payload || {});
      const result = await inquiriesService.updateInquiry(inquiryId, normalized);
      if (result.success) {
        setInquiries(prev => prev.map(i => (String(i.id) === String(inquiryId) ? { ...i, ...result.data } : i)));
        showUpdate(i18n.t('inquiriesModule.inquiries.notifications.updated', { id: String(inquiryId) }));
      } else {
        setError(result.error);
      }
      return result;
    } catch (err) {
      const errorMessage = err.message;
      setError(errorMessage);
      return { success: false, error: errorMessage };
    }
  }, [showUpdate]);

  const updateInquiryStatus = useCallback(async (inquiryId, statusPayload) => {
    setError(null);

    // Determine the requested status from the payload
    const requestedStatus = typeof statusPayload === 'string'
      ? statusPayload
      : statusPayload && (statusPayload.new_status || statusPayload.newStatus || statusPayload.status);

    try {
      // Optimistically update the local state immediately
      setInquiries(prev => prev.map(i =>
        String(i.id) === String(inquiryId)
          ? { ...i, status: requestedStatus }
          : i
      ));

      const result = await inquiriesService.updateInquiryStatus(inquiryId, statusPayload);

      if (result.success) {
        if (result.data && typeof result.data === 'object' && Object.keys(result.data).length > 0) {
          setInquiries(prev => prev.map(i =>
            String(i.id) === String(inquiryId)
              ? { ...i, ...result.data }
              : i
          ));
        }

        showUpdate(i18n.t('inquiriesModule.inquiries.notifications.statusUpdated', { id: String(inquiryId) }));
      } else {
        setInquiries(prev => prev.map(i => {
          if (String(i.id) !== String(inquiryId)) return i;
          return i;
        }));
        setError(result.error);
      }
      return result;
    } catch (err) {
      setInquiries(prev => prev.map(i => {
        if (String(i.id) !== String(inquiryId)) return i;
        return i;
      }));

      const errorMessage = err.message;
      setError(errorMessage);
      return { success: false, error: errorMessage };
    }
  }, [showUpdate]);

  const deleteInquiry = useCallback(async (inquiryId) => {
    setError(null);
    try {
      const result = await inquiriesService.deleteInquiry(inquiryId);
      if (result.success) {
        setInquiries(prev => prev.filter(i => String(i.id) !== String(inquiryId)));
        setTotalCount(prev => Math.max(0, prev - 1));
        showDelete(i18n.t('inquiriesModule.inquiries.notifications.deleted', { id: String(inquiryId) }));
      } else {
        setError(result.error);
      }
      return result;
    } catch (err) {
      const errorMessage = err.message;
      setError(errorMessage);
      return { success: false, error: errorMessage };
    }
  }, [showDelete]);

  const bulkDeleteInquiries = useCallback(async (ids) => {
    if (!Array.isArray(ids) || ids.length === 0) return { success: false, error: i18n.t('inquiriesModule.inquiries.errors.noneSelected') };
    setError(null);
    try {
      const result = await inquiriesService.bulkDeleteInquiries(ids);
      if (result.success) {
        const idSet = new Set(ids.map((id) => String(id)));
        setInquiries(prev => prev.filter(i => !idSet.has(String(i.id))));
        setTotalCount(prev => Math.max(0, prev - ids.length));
        showDelete(i18n.t('inquiriesModule.inquiries.notifications.bulkDeleted', { count: String(ids.length) }));
      } else {
        setError(result.error);
      }
      return result;
    } catch (err) {
      const errorMessage = err.message;
      setError(errorMessage);
      return { success: false, error: errorMessage };
    }
  }, [showDelete]);

  const sendQuoteEmail = useCallback(async (inquiryId) => {
    try {
      const result = await inquiriesService.sendQuoteEmail(inquiryId);
      if (result.success) {
        showSuccess(i18n.t('inquiriesModule.inquiries.notifications.emailSent'));
      }
      return result;
    } catch (err) {
      return { success: false, error: err.message };
    }
  }, [showSuccess]);

  const sendQuoteNotification = useCallback(async (inquiryId, type = 'email') => {
    try {
      const result = await inquiriesService.sendQuoteNotification(inquiryId, type);
      if (result.success) {
        const msg = type === 'whatsapp'
          ? (i18n.t('inquiriesModule.inquiries.notifications.whatsappSent'))
          : (i18n.t('inquiriesModule.inquiries.notifications.emailSent'));
        showSuccess(msg);
      }
      return result;
    } catch (err) {
      return { success: false, error: err.message };
    }
  }, [showSuccess]);

  const clearError = useCallback(() => setError(null), []);

  const updateFilters = useCallback((newFilters) => {
    setFilters(prev => ({ ...prev, ...newFilters }));
    setCurrentPage(1);
  }, []);

  const goToPage = useCallback((page) => {
    if (page >= 1 && page <= totalPages && page !== currentPage) {
      setCurrentPage(page);
    }
  }, [currentPage, totalPages]);

  const nextPage = useCallback(() => {
    goToPage(currentPage + 1);
  }, [currentPage, goToPage]);

  const prevPage = useCallback(() => {
    goToPage(currentPage - 1);
  }, [currentPage, goToPage]);

  // Auto-fetch on mount and when page or filters change
  useEffect(() => {
    fetchInquiries();
  }, [fetchInquiries, currentPage, filters, selectedWarehouseIds]);

  return {
    // State
    inquiries,
    loading,
    error,
    currentPage,
    totalPages,
    totalCount,
    filters,
    // Product sizes reference
    productSizes,
    fetchProductSizes,
    // Actions
    fetchInquiries,
    getInquiryById,
    createInquiry,
    updateInquiry,
    updateInquiryStatus,
    deleteInquiry,
    bulkDeleteInquiries,
    sendQuoteEmail,
    sendQuoteNotification,
    clearError,
    // Pagination
    updateFilters,
    goToPage,
    nextPage,
    prevPage,
    setPageSize,
  };
};

export default useInquiries;
